import React, { useState, useEffect, useMemo } from 'react';
import useRestrictions from '../../../hooks/useRestrictions';
import useDevelopmentCalculator from '../../../hooks/useDevelopmentCalculator';
import useHmoData from '../../../hooks/useHmoData';
import PlanningDetail from './PlanningDetail';
import { InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Text,
  List,
  ListItem,
  Skeleton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  Stack,
  Icon,
  Flex,
  CircularProgress,
  CircularProgressLabel,
  Card,
  CardHeader,
  CardBody,
  Select,
  Spinner,
  Center,
  HStack,
  SimpleGrid,
  Badge,
  VStack,
  Button,
} from '@chakra-ui/react';
import {
  FaBuilding,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaMoneyBillWave,
  FaCrown,
  FaLock,
  FaLocationArrow,
} from 'react-icons/fa';

// Format dataset name for display
const formatDatasetName = (name) => {
  return name
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format category name for display
const formatCategoryName = (category) => {
  if (!category) return 'Unknown';
  return category
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Function to get AI planning analysis

const Planning = ({
  applications = [],
  isLoading = false,
  error = null,
  validatedAddress = {},
  location = {},
  currentUser = null,
  navigate = () => {},
  planningAnalysis = '',
  isAnalysisLoading,
}) => {
  // Use our restrictions hook to fetch the data
  const {
    data: restrictions,
    isLoading: restrictionsLoading,
    error: restrictionsError,
  } = useRestrictions(location);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // State for Development Profit Calculator
  const [calculatorInputs, setCalculatorInputs] = useState({
    purchasePrice: 1000000,
    sqftPreDevelopment: 2000,
    sqftPostDevelopment: 3000,
    projectType: 'refurbish',
    finishQuality: 'medium',
  });

  // Use our custom hook for development calculator
  const {
    isLoading: devCalcLoading,
    error: devCalcError,
    data: devCalcData,
    mutate: calculate,
  } = useDevelopmentCalculator();

  // Use our custom hook for HMO data
  const {
    isLoading: hmoLoading,
    error: hmoError,
    data: hmoData,
    refetch,
  } = useHmoData(validatedAddress?.postcode, 5);

  console.log('currentUser', currentUser);
  // Fetch HMO data when postcode is available and user is premium
  useEffect(() => {
    if (validatedAddress?.postcode && currentUser?.subscription === 'premium') {
      refetch(validatedAddress.postcode);
    }
  }, [validatedAddress?.postcode, currentUser?.subscription, refetch]);

  // Extract results from API data
  const calculatorResults = useMemo(() => {
    if (!devCalcData || !devCalcData.results) {
      return {
        totalCosts: '0',
        totalValue: '0',
        profit: '0',
        profitPc: '0%',
      };
    }

    const results = devCalcData.results;
    return {
      totalCosts: results.totalCosts?.value || '0',
      totalValue: results.totalValue?.value || '0',
      profit: results.profit?.value || '0',
      profitPc: results.profitPc?.value || '0%',
    };
  }, [devCalcData]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    // Convert to number and handle empty inputs
    const numValue = value === '' ? 0 : Number(value.replace(/,/g, ''));
    setCalculatorInputs((prev) => ({
      ...prev,
      [field]: numValue,
    }));
  };

  // Format currency for display
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB').format(value);
  };
  const [street, setStreet] = useState({
    display: '',
    search: '',
  });
  const [calcError, setCalcError] = useState(null);

  useEffect(() => {
    console.log('Planning component - applications:', applications);
    console.log('Planning component - isLoading:', isLoading);
    console.log('Planning component - error:', error);
    console.log('Planning component - validatedAddress:', validatedAddress);

    // Generate AI planning analysis when data is available

    // generatePlanningAnalysis();
  }, [applications, isLoading, error, validatedAddress, restrictions]);

  // Update street when validatedAddress changes
  useEffect(() => {
    console.log('validatedAddress changed:', validatedAddress);
    if (validatedAddress?.street) {
      console.log('Setting street with:', validatedAddress.street);
      setStreet({
        display: validatedAddress.street,
        search: validatedAddress.street.toLowerCase(),
      });
    }
  }, [validatedAddress]);

  // Filter applications by decision and street
  const approvedApplications = applications
    .filter(
      (app) =>
        app.decision?.toLowerCase().includes('grant') ||
        app.decision?.toLowerCase().includes('no objection') ||
        app.status?.toLowerCase() === 'granted' ||
        app.status?.toLowerCase() === 'no objection',
    )
    .sort(
      (a, b) => new Date(b.application_date || b.date) - new Date(a.application_date || a.date),
    );

  // Filter applications by street
  const streetApplications = applications
    .filter((app) => {
      const appStreet = app.street_only || '';
      return street.display && appStreet.toLowerCase().includes(street.display.toLowerCase());
    })
    .sort(
      (a, b) => new Date(b.application_date || b.date) - new Date(a.application_date || a.date),
    );

  // Calculate success rates by category
  const categorySummary = useMemo(() => {
    const categories = {};

    // Group applications by category
    applications.forEach((app) => {
      const category = app.category || 'Unknown';
      if (!categories[category]) {
        categories[category] = {
          name: category,
          total: 0,
          approved: 0,
          applications: [],
        };
      }

      categories[category].total += 1;
      categories[category].applications.push(app);

      // Count approved applications
      if (
        app.decision?.toLowerCase().includes('grant') ||
        app.decision?.toLowerCase().includes('no objection') ||
        app.status?.toLowerCase() === 'granted' ||
        app.status?.toLowerCase() === 'no objection'
      ) {
        categories[category].approved += 1;
      }
    });

    // Calculate success rates
    Object.values(categories).forEach((category) => {
      category.success_rate =
        category.total > 0 ? Math.round((category.approved / category.total) * 100) : 0;
    });

    // Convert to array and sort by total applications (descending)
    return Object.values(categories)
      .sort((a, b) => b.total - a.total)
      .slice(0, 6); // Limit to top 6 categories
  }, [applications]);

  // Get color based on success rate
  const getSuccessColor = (rate) => {
    if (rate >= 70) return 'green.400';
    if (rate >= 50) return 'orange.400';
    return 'red.400';
  };

  // Handle back button click
  const handleBackClick = () => {
    setSelectedApplication(null);
  };

  // If we have a selected application, show the detail view
  if (selectedApplication) {
    return (
      <PlanningDetail
        application={selectedApplication}
        onBack={handleBackClick}
        formatCategoryName={formatCategoryName}
      />
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <Center py={6}>
        <Spinner color='brand.primary' size='lg' />
      </Center>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert status='error' borderRadius='md'>
        <AlertIcon />
        <Text>Error loading planning data: {error}</Text>
      </Alert>
    );
  }

  // Show empty state if no applications
  if (!applications || applications.length === 0) {
    return (
      <Alert status='info' borderRadius='md'>
        <AlertIcon />
        <Text>No planning applications found in this area.</Text>
      </Alert>
    );
  }

  // Show main view with applications
  return (
    <Stack spacing={6} width='100%'>
      {/* AI Planning Analysis - Full Width Box */}
      {(planningAnalysis || isAnalysisLoading) && (
        <Card variant='outline' bg='blue.50' width='100%'>
          <CardBody>
            <HStack spacing={2} mb={2}>
              <InfoIcon color='blue.500' />
              <Heading size='sm'>AI Planning Insights</Heading>
            </HStack>
            {isAnalysisLoading ? (
              <HStack spacing={2}>
                <Spinner size='sm' color='blue.500' />
                <Text fontSize='sm'>Generating planning insights...</Text>
              </HStack>
            ) : (
              <Text fontSize='sm'>{planningAnalysis}</Text>
            )}
          </CardBody>
        </Card>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} width='100%'>
        <Card variant='outline' mb={8} bg='purple.50'>
          <CardHeader>
            <Heading size='md'>Planning Success Rates</Heading>
            <Text fontSize='sm'>Success rates by application type in your area</Text>
          </CardHeader>
          <CardBody>
            {/* Authority Planning Statistics */}
            {applications.length > 0 && applications[0]?.authority_stats && (
              <Box p={3} bg='white' borderRadius='md' mb={4} boxShadow='sm'>
                <Heading size='sm' mb={2}>
                  Local Authority Planning Statistics
                </Heading>
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <Text fontSize='sm' color='gray.600'>
                      Local Authority
                    </Text>
                    <Text fontWeight='bold'>{applications[0].authority_stats.authority_name}</Text>
                    <HStack mt={2}>
                      <CircularProgress
                        value={applications[0].authority_stats.decisions_granted_percentage}
                        color='green.400'
                        size='60px'
                      >
                        <CircularProgressLabel>
                          {applications[0].authority_stats.decisions_granted_percentage}%
                        </CircularProgressLabel>
                      </CircularProgress>
                      <VStack align='start' spacing={0}>
                        <Text fontSize='sm'>Success Rate</Text>
                        <Text fontSize='xs' color='gray.500'>
                          {applications[0].authority_stats.applications_decided} applications
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                  <Box>
                    <Text fontSize='sm' color='gray.600'>
                      National Average
                    </Text>
                    <Text fontWeight='bold'>England</Text>
                    <HStack mt={2}>
                      <CircularProgress
                        value={
                          applications[0].authority_stats.national_decisions_granted_percentage
                        }
                        color='blue.400'
                        size='60px'
                      >
                        <CircularProgressLabel>
                          {applications[0].authority_stats.national_decisions_granted_percentage}%
                        </CircularProgressLabel>
                      </CircularProgress>
                      <VStack align='start' spacing={0}>
                        <Text fontSize='sm'>Success Rate</Text>
                        <Text fontSize='xs' color='gray.500'>
                          {applications[0].authority_stats.national_applications_decided}{' '}
                          applications
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                </SimpleGrid>
              </Box>
            )}

            {categorySummary.length === 0 ? (
              <Alert status='info' borderRadius='md'>
                <AlertIcon />
                <Text>No application data available</Text>
              </Alert>
            ) : (
              <Stack spacing={4}>
                {/* Average Success Rate Summary */}
                {categorySummary.length > 0 && (
                  <Box mb={4}>
                    <HStack justify='space-between' align='center' mb={2}>
                      <VStack align='start' spacing={0}>
                        <Text fontWeight='bold' fontSize='md'>
                          Average Planning Approval Rate
                        </Text>
                        <Text fontSize='sm' color='gray.600'>
                          Across all application types
                        </Text>
                      </VStack>
                      <CircularProgress
                        value={
                          categorySummary.reduce((sum, cat) => sum + cat.success_rate, 0) /
                          categorySummary.length
                        }
                        color={getSuccessColor(
                          categorySummary.reduce((sum, cat) => sum + cat.success_rate, 0) /
                            categorySummary.length,
                        )}
                        size='60px'
                      >
                        <CircularProgressLabel>
                          {Math.round(
                            categorySummary.reduce((sum, cat) => sum + cat.success_rate, 0) /
                              categorySummary.length,
                          )}
                          %
                        </CircularProgressLabel>
                      </CircularProgress>
                    </HStack>

                    {applications.length > 0 && applications[0]?.authority_stats && (
                      <HStack justify='flex-end'>
                        <Text fontWeight='bold' fontSize='md'>
                          Compared to {applications[0].authority_stats.authority_name} average:{' '}
                        </Text>
                        {(() => {
                          // Calculate the difference between the average success rate and local authority rate
                          const avgSuccessRate = Math.round(
                            categorySummary.reduce((sum, cat) => sum + cat.success_rate, 0) /
                              categorySummary.length,
                          );
                          const authorityRate =
                            applications[0].authority_stats.decisions_granted_percentage;
                          const difference = avgSuccessRate - authorityRate;
                          const sign = difference > 0 ? '+' : '';

                          // Determine the color based on the difference
                          // Higher than authority average is good (green), lower is not as good (red)
                          const colorScheme =
                            difference > 0 ? 'green' : difference < 0 ? 'red' : 'gray';

                          return (
                            <Badge colorScheme={colorScheme} fontSize='sm'>
                              {sign}
                              {difference}%
                            </Badge>
                          );
                        })()}
                      </HStack>
                    )}
                  </Box>
                )}

                {categorySummary.map((category, index) => (
                  <Box
                    key={index}
                    p={4}
                    borderWidth='1px'
                    borderRadius='lg'
                    bg='white'
                    _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                    onClick={() => {
                      // Get the first application from the category as the primary one
                      const app = category.applications[0];

                      if (app) {
                        // Find all applications in the same category from all applications
                        const categoryApplications = applications.filter(
                          (a) => a.category === app.category,
                        );

                        // Set the selected application and pass all applications in the same category
                        setSelectedApplication({
                          ...app,
                          categoryApplications: categoryApplications,
                        });
                      }
                    }}
                  >
                    <HStack justify='space-between' align='center'>
                      <VStack align='start' spacing={1}>
                        <Text fontWeight='bold' fontSize='md'>
                          {formatCategoryName(category.name)}
                        </Text>
                        <Text fontSize='sm' color='gray.600'>
                          Total Applications: {category.total}
                        </Text>
                      </VStack>
                      <CircularProgress
                        value={category.success_rate}
                        color={getSuccessColor(category.success_rate)}
                        size='60px'
                      >
                        <CircularProgressLabel>{category.success_rate}%</CircularProgressLabel>
                      </CircularProgress>
                    </HStack>
                  </Box>
                ))}
              </Stack>
            )}
          </CardBody>
        </Card>

        <Card variant='outline' mb={8} bg='purple.50'>
          <CardHeader>
            <Heading size='md'>Recent Street Applications</Heading>
            <Text fontSize='sm'>
              Recent planning applications on {street.display || 'your street'}
            </Text>
          </CardHeader>
          <CardBody>
            {streetApplications.length === 0 ? (
              <Alert status='info' borderRadius='md'>
                <AlertIcon />
                <Text>No applications found on this street</Text>
              </Alert>
            ) : (
              <Stack spacing={4}>
                {streetApplications.slice(0, 5).map((app, index) => (
                  <Box
                    key={index}
                    p={4}
                    borderWidth='1px'
                    borderRadius='lg'
                    bg='white'
                    _hover={{ bg: 'blue.100', cursor: 'pointer' }}
                    onClick={() => {
                      // Find all applications in the same category from all applications, not just street applications
                      const categoryApplications = applications.filter(
                        (a) => a.category === app.category,
                      );

                      // Debug the applications
                      console.log('Planning - app:', app);
                      console.log('Planning - app.category:', app.category);
                      console.log('Planning - applications count:', applications.length);
                      console.log(
                        'Planning - categoryApplications count:',
                        categoryApplications.length,
                      );

                      // Set the selected application and pass all applications in the same category
                      setSelectedApplication({
                        ...app,
                        categoryApplications: categoryApplications,
                      });
                    }}
                  >
                    <Stack spacing={2}>
                      <Text fontWeight='bold' fontSize='md'>
                        {formatCategoryName(app.category || 'unknown')}
                      </Text>
                      <Text fontSize='xs' color='gray.600' fontWeight='bold'>
                        {app.location_details || 'No address available'}
                      </Text>
                      <Text fontSize='xs' color='gray.600'>
                        {app.proposal || 'No proposal details'}
                      </Text>
                      <Text noOfLines={2} fontSize='sm'>
                        {app.description}
                      </Text>
                      <HStack justifyContent='space-between'>
                        <Badge
                          colorScheme={
                            app.decision?.toLowerCase().includes('grant') ||
                            app.decision?.toLowerCase().includes('no objection') ||
                            app.status?.toLowerCase() === 'granted' ||
                            app.status?.toLowerCase() === 'no objection'
                              ? 'green'
                              : app.decision?.toLowerCase().includes('refuse') ||
                                app.status?.toLowerCase() === 'refused'
                              ? 'red'
                              : 'gray'
                          }
                        >
                          {app.decision || app.status || 'Pending'}
                        </Badge>
                        <Text fontSize='xs' color='gray.500'>
                          {new Date(app.application_date || app.date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </Text>
                      </HStack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </CardBody>
        </Card>

        <Card variant='outline' mb={8} bg='purple.50'>
          <CardHeader>
            <Heading size='md'>Council Restrictions</Heading>
            <Text fontSize='sm'>Planning restrictions that may affect development</Text>
          </CardHeader>
          <CardBody>
            {restrictionsLoading ? (
              <Center py={4}>
                <Spinner color='brand.primary' size='md' />
              </Center>
            ) : restrictionsError ? (
              <Alert status='error' borderRadius='md'>
                <AlertIcon />
                <Text>Error loading restrictions data: {restrictionsError}</Text>
              </Alert>
            ) : (
              <SimpleGrid columns={1} spacing={4}>
                {restrictions.length > 0 ? (
                  restrictions.map((restriction, index) => (
                    <Box key={index} p={3} borderWidth='1px' borderRadius='md' bg='white'>
                      <HStack justify='space-between' mb={2}>
                        <Text fontWeight='bold'>{formatDatasetName(restriction.dataset)}</Text>
                        <Icon as={FaCheck} color='green.500' boxSize={4} />
                      </HStack>
                      {/* Display the description */}
                      {restriction.description && (
                        <Text fontSize='sm' color='gray.600' mb={2}>
                          {restriction.description}
                        </Text>
                      )}
                      {restriction.entities && restriction.entities.length > 0 && (
                        <Box pl={2} borderLeftWidth='2px' borderLeftColor='purple.200'>
                          {restriction.entities.map((entity, entityIndex) => (
                            <Text key={entityIndex} fontSize='sm' color='gray.700' mt={1}>
                              {entity}
                            </Text>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))
                ) : (
                  <Text textAlign='center' color='gray.500'>
                    No restriction data available for this property
                  </Text>
                )}
              </SimpleGrid>
            )}
          </CardBody>
        </Card>

        {/* Multiple Occupancy Houses */}
        <Card variant='outline' mb={8} bg='purple.50'>
          <CardHeader p={6} borderBottom='1px' borderColor='gray.100'>
            <HStack justify='space-between' width='full'>
              <HStack spacing={2}>
                <Icon as={FaBuilding} boxSize={5} color='brand.primary' />
                <Heading size='sm' color='brand.text'>
                  Multiple Occupancy Houses
                </Heading>
              </HStack>
              <Badge colorScheme='purple' p={1}>
                <HStack spacing={1}>
                  <Icon as={FaCrown} />
                  <Text>Premium Feature</Text>
                </HStack>
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            {currentUser?.subscription === 'premium' ? (
              <Box p={4}>
                {hmoLoading ? (
                  <Center p={4}>
                    <Spinner color='brand.primary' />
                  </Center>
                ) : hmoError ? (
                  <Alert status='error' borderRadius='md'>
                    <AlertIcon />
                    <AlertTitle>Error</AlertTitle>
                    <Text>Unable to fetch Houses in Multiple Occupation data</Text>
                  </Alert>
                ) : !hmoData ||
                  !hmoData.data ||
                  !hmoData.data.data ||
                  !hmoData.data.data.hmos ||
                  hmoData.data.data.hmos.length === 0 ? (
                  <Alert status='info' borderRadius='md'>
                    <AlertIcon />
                    <AlertTitle>No HMO Data</AlertTitle>
                    <Text>No Houses in Multiple Occupation found for this postcode.</Text>
                  </Alert>
                ) : (
                  <Box>
                    {/* Filter HMOs by street */}
                    {(() => {
                      // Extract street name from validated address
                      const streetName = validatedAddress?.street || '';

                      // Find maximum distance in miles
                      const maxDistance =
                        hmoData.data.data.hmos.length > 0
                          ? Math.max(
                              ...hmoData.data.data.hmos.map(
                                (hmo) => parseFloat(hmo.distance_miles) || 0,
                              ),
                            ).toFixed(1)
                          : 0;

                      // Filter HMOs on the same street
                      const sameStreetHmos = hmoData.data.data.hmos.filter((hmo) => {
                        // Check if the HMO address contains the street name
                        return (
                          streetName && hmo.address.toLowerCase().includes(streetName.toLowerCase())
                        );
                      });

                      // Sort all HMOs by distance
                      const sortedHmos = [...hmoData.data.data.hmos].sort((a, b) => {
                        return parseFloat(a.distance_miles) - parseFloat(b.distance_miles);
                      });

                      // Get only the nearest 5 HMOs
                      const nearestHmos = sortedHmos.slice(0, 5);

                      // If no HMOs on the same street, show the nearest 5 HMOs
                      const hmosToShow = sameStreetHmos.length > 0 ? sameStreetHmos : nearestHmos;

                      return (
                        <>
                          <Box mb={4} p={3} borderRadius='md' bg='purple.100'>
                            <Text fontWeight='semibold' textAlign='center'>
                              Found {hmoData.data.data.hmos.length} Houses in Multiple Occupation
                              within {maxDistance} miles
                            </Text>
                          </Box>
                          <Flex justify='space-between' align='center' mb={4}>
                            <Text fontWeight='bold'>
                              {sameStreetHmos.length > 0
                                ? `Houses in Multiple Occupation on ${streetName}`
                                : streetName
                                ? `No HMOs on ${streetName}`
                                : 'Houses in Multiple Occupation Nearby'}
                            </Text>
                            <Badge colorScheme='purple'>{hmosToShow.length} Found</Badge>
                          </Flex>
                          <List spacing={3}>
                            {hmosToShow.map((hmo, index) => (
                              <ListItem
                                key={index}
                                p={3}
                                borderWidth='1px'
                                borderRadius='md'
                                bg='white'
                              >
                                <Text fontWeight='semibold'>{hmo.address}</Text>
                                <HStack mt={2} spacing={4}>
                                  <Flex align='center'>
                                    <Icon as={FaBuilding} color='purple.500' mr={1} />
                                    <Text fontSize='sm'>Council: {hmo.council}</Text>
                                  </Flex>
                                  <Flex align='center'>
                                    <Icon as={FaLocationArrow} color='green.500' mr={1} />
                                    <Text fontSize='sm'>Distance: {hmo.distance_miles} miles</Text>
                                  </Flex>
                                </HStack>
                                <HStack mt={2} spacing={4}>
                                  <Flex align='center'>
                                    <Icon as={FaCrown} color='yellow.500' mr={1} />
                                    <Text fontSize='sm'>
                                      License Expires: {hmo?.licence_expiry}
                                    </Text>
                                  </Flex>
                                  <Flex align='center'>
                                    <Text fontSize='sm'>Ref: {hmo.reference}</Text>
                                  </Flex>
                                </HStack>
                              </ListItem>
                            ))}
                          </List>
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            ) : (
              <VStack spacing={4} align='center' py={8}>
                <Icon as={FaLock} boxSize={8} color='gray.400' />
                <Text color='gray.600' fontSize='sm' textAlign='center'>
                  Multiple Occupancy Houses analysis is only available for premium subscribers
                </Text>
                <HStack spacing={2}>
                  <Button
                    size='sm'
                    colorScheme='purple'
                    leftIcon={<Icon as={FaCrown} />}
                    onClick={() => navigate('/subscription')}
                  >
                    Upgrade to Premium
                  </Button>
                  {/* This button is for testing only - would be removed in production */}
                  <Button
                    size='sm'
                    colorScheme='gray'
                    onClick={() => {
                      // For testing purposes only
                      console.log('Setting test user as premium');
                      if (currentUser) {
                        currentUser.subscription = 'premium';
                        // Force re-render
                        setSelectedApplication(null);
                      }
                    }}
                  >
                    Test Premium
                  </Button>
                </HStack>
              </VStack>
            )}
          </CardBody>
        </Card>

        {/* Development Profit Calculator */}
        <Card variant='outline' mb={8} bg='purple.50' gridColumn={{ md: 'span 2' }}>
          <CardHeader p={6} borderBottom='1px' borderColor='gray.100'>
            <VStack align='stretch' spacing={2}>
              <HStack justify='space-between' width='full'>
                <HStack spacing={2}>
                  <Icon as={FaMoneyBillWave} boxSize={5} color='brand.primary' />
                  <Heading size='sm' color='brand.text'>
                    Development Profit Calculator
                  </Heading>
                </HStack>
                <Badge colorScheme='purple' p={1}>
                  <HStack spacing={1}>
                    <Icon as={FaCrown} />
                    <Text>Premium Feature</Text>
                  </HStack>
                </Badge>
              </HStack>
              <Text fontSize='sm' color='gray.600'>
                Estimate potential profits from property development projects. Click on values to
                edit them.
              </Text>
            </VStack>
          </CardHeader>
          <CardBody>
            {currentUser?.subscription === 'premium' ? (
              <Box p={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box>
                    <Text fontWeight='bold' mb={2}>
                      Property Details
                    </Text>
                    <VStack spacing={3} align='stretch'>
                      <Box>
                        <Text fontSize='sm' mb={1}>
                          Purchase Price (£)
                        </Text>
                        <Box
                          p={2}
                          borderWidth='1px'
                          borderRadius='md'
                          bg='white'
                          contentEditable='true'
                          suppressContentEditableWarning={true}
                          onBlur={(e) => handleInputChange('purchasePrice', e.target.innerText)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.target.blur();
                            }
                          }}
                        >
                          {formatCurrency(calculatorInputs.purchasePrice)}
                        </Box>
                      </Box>
                      <Box>
                        <Text fontSize='sm' mb={1}>
                          Pre-Development Size (sq ft)
                        </Text>
                        <Box
                          p={2}
                          borderWidth='1px'
                          borderRadius='md'
                          bg='white'
                          contentEditable='true'
                          suppressContentEditableWarning={true}
                          onBlur={(e) =>
                            handleInputChange('sqftPreDevelopment', e.target.innerText)
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.target.blur();
                            }
                          }}
                        >
                          {formatCurrency(calculatorInputs.sqftPreDevelopment)}
                        </Box>
                      </Box>
                      <Box>
                        <Text fontSize='sm' mb={1}>
                          Post-Development Size (sq ft)
                        </Text>
                        <Box
                          p={2}
                          borderWidth='1px'
                          borderRadius='md'
                          bg='white'
                          contentEditable='true'
                          suppressContentEditableWarning={true}
                          onBlur={(e) =>
                            handleInputChange('sqftPostDevelopment', e.target.innerText)
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.target.blur();
                            }
                          }}
                        >
                          {formatCurrency(calculatorInputs.sqftPostDevelopment)}
                        </Box>
                      </Box>
                      <Box>
                        <Text fontSize='sm' mb={1}>
                          Project Type
                        </Text>
                        <Select
                          value={calculatorInputs.projectType}
                          onChange={(e) => handleInputChange('projectType', e.target.value)}
                          bg='white'
                          borderRadius='md'
                        >
                          <option value='refurbish'>Refurbish</option>
                          <option value='demolition'>Demolition</option>
                        </Select>
                      </Box>
                      <Box>
                        <Text fontSize='sm' mb={1}>
                          Finish Quality
                        </Text>
                        <Select
                          value={calculatorInputs.finishQuality}
                          onChange={(e) => handleInputChange('finishQuality', e.target.value)}
                          bg='white'
                          borderRadius='md'
                        >
                          <option value='basic'>Basic</option>
                          <option value='medium'>Medium</option>
                          <option value='high'>High</option>
                          <option value='premium'>Premium</option>
                        </Select>
                      </Box>
                      <Box mt={4}>
                        <Button
                          colorScheme='purple'
                          width='full'
                          leftIcon={<Icon as={FaMoneyBillWave} />}
                          isLoading={devCalcLoading}
                          onClick={() => {
                            if (validatedAddress?.postcode) {
                              calculate({
                                postcode: validatedAddress.postcode,
                                purchasePrice: calculatorInputs.purchasePrice,
                                sqftPreDevelopment: calculatorInputs.sqftPreDevelopment,
                                sqftPostDevelopment: calculatorInputs.sqftPostDevelopment,
                                projectType: calculatorInputs.projectType,
                                finishQuality: calculatorInputs.finishQuality,
                              });
                            } else {
                              setCalcError('Postcode is required');
                            }
                          }}
                        >
                          Calculate Profit
                        </Button>
                      </Box>
                    </VStack>
                  </Box>
                  <Box>
                    <Text fontWeight='bold' mb={2}>
                      Profit Analysis
                    </Text>
                    {devCalcLoading ? (
                      <Center p={4}>
                        <Spinner color='brand.primary' />
                      </Center>
                    ) : devCalcError ? (
                      <Alert status='error' borderRadius='md'>
                        <AlertIcon />
                        <AlertTitle>Error</AlertTitle>
                        <Text>{devCalcError}</Text>
                      </Alert>
                    ) : calcError ? (
                      <Alert status='error' borderRadius='md'>
                        <AlertIcon />
                        <AlertTitle>Error</AlertTitle>
                        <Text>{calcError}</Text>
                      </Alert>
                    ) : (
                      <VStack spacing={3} align='stretch'>
                        <HStack
                          justify='space-between'
                          p={2}
                          borderWidth='1px'
                          borderRadius='md'
                          bg='white'
                        >
                          <Text>Total Costs</Text>
                          <Text fontWeight='bold'>{calculatorResults.totalCosts}</Text>
                        </HStack>
                        <HStack
                          justify='space-between'
                          p={2}
                          borderWidth='1px'
                          borderRadius='md'
                          bg='white'
                        >
                          <Text>Total Value</Text>
                          <Text fontWeight='bold'>{calculatorResults.totalValue}</Text>
                        </HStack>
                        <HStack
                          justify='space-between'
                          p={2}
                          borderWidth='1px'
                          borderRadius='md'
                          bg='white'
                        >
                          <Text>Profit</Text>
                          <Text
                            fontWeight='bold'
                            color={
                              calculatorResults.profit.startsWith('-') ? 'red.500' : 'green.500'
                            }
                          >
                            {calculatorResults.profit}
                          </Text>
                        </HStack>
                        <HStack
                          justify='space-between'
                          p={2}
                          borderWidth='1px'
                          borderRadius='md'
                          bg='white'
                        >
                          <Text>ROI</Text>
                          <Text
                            fontWeight='bold'
                            color={
                              calculatorResults.profitPc.startsWith('-') ? 'red.500' : 'green.500'
                            }
                          >
                            {calculatorResults.profitPc}
                          </Text>
                        </HStack>
                      </VStack>
                    )}
                  </Box>
                </SimpleGrid>
              </Box>
            ) : (
              <VStack spacing={4} align='center' py={8}>
                <Icon as={FaLock} boxSize={8} color='gray.400' />
                <Text color='gray.600' fontSize='sm' textAlign='center'>
                  The Development Profit Calculator is only available for premium subscribers
                </Text>
                <HStack spacing={2}>
                  <Button
                    size='sm'
                    colorScheme='purple'
                    leftIcon={<Icon as={FaCrown} />}
                    onClick={() => navigate('/subscription')}
                  >
                    Upgrade to Premium
                  </Button>
                  {/* This button is for testing only - would be removed in production */}
                  <Button
                    size='sm'
                    colorScheme='gray'
                    onClick={() => {
                      // For testing purposes only
                      console.log('Setting test user as premium');
                      if (currentUser) {
                        currentUser.subscription = 'premium';
                        // Force re-render
                        setSelectedApplication(null);
                      }
                    }}
                  >
                    Test Premium
                  </Button>
                </HStack>
              </VStack>
            )}
          </CardBody>
        </Card>
      </SimpleGrid>
    </Stack>
  );
};

export default Planning;
